import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

interface AdminAuthUser {
  id: string;
  username: string;
  role: string;
  isActive?: boolean;
  lastLoginAt?: string | null;
}

interface AdminAuthSuccessResponse {
  success: boolean;
  message: string;
  data?: {
    accessToken?: string;
    tokenType?: string;
    expiresIn?: string;
    user?: AdminAuthUser;
  };
}

interface AdminProfileResponse {
  success: boolean;
  message: string;
  data: AdminAuthUser;
}

interface ApiErrorMessageObject {
  code?: string;
  message?: string;
}

interface ApiErrorShape {
  statusCode?: number;
  message?: string | ApiErrorMessageObject;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private readonly tokenStorageKey = 'admin-auth-token';
  private readonly userStorageKey = 'admin-auth-user';
  private readonly authBaseUrl = `${environment.apiUrl}/auth/admin`;

  constructor(private http: HttpClient) {}

  login(username: string, password: string): Observable<string> {
    return this.http
      .post<AdminAuthSuccessResponse>(`${this.authBaseUrl}/login`, { username, password })
      .pipe(map((response) => this.storeTokenFromResponse(response)));
  }

  register(username: string, password: string, confirmPassword: string): Observable<string> {
    return this.http
      .post<AdminAuthSuccessResponse>(`${this.authBaseUrl}/register`, {
        username,
        password,
        confirmPassword,
      })
      .pipe(map((response) => this.storeTokenFromResponse(response)));
  }

  getMe(): Observable<AdminAuthUser> {
    const token = this.getToken();

    return this.http
      .get<AdminProfileResponse>(`${this.authBaseUrl}/me`, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      })
      .pipe(
        map((response) => {
          sessionStorage.setItem(this.userStorageKey, JSON.stringify(response.data));
          return response.data;
        }),
      );
  }

  logout(): void {
    sessionStorage.removeItem(this.tokenStorageKey);
    sessionStorage.removeItem(this.userStorageKey);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken(): string {
    return sessionStorage.getItem(this.tokenStorageKey) ?? '';
  }

  setToken(token: string): void {
    sessionStorage.setItem(this.tokenStorageKey, token);
  }

  getCurrentUser(): AdminAuthUser | null {
    const storedValue = sessionStorage.getItem(this.userStorageKey);
    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as AdminAuthUser;
    } catch {
      sessionStorage.removeItem(this.userStorageKey);
      return null;
    }
  }

  getErrorMessage(error: unknown, fallback: string): string {
    const candidate = error as {
      status?: number;
      error?: ApiErrorShape;
      message?: string;
    };

    const statusCode = candidate?.error?.statusCode ?? candidate?.status;
    const apiMessage = candidate?.error?.message;

    if (statusCode === 401) {
      if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
      }

      if (
        apiMessage &&
        typeof apiMessage === 'object' &&
        typeof apiMessage.message === 'string' &&
        apiMessage.message.trim()
      ) {
        return apiMessage.message;
      }

      return 'Invalid username or password';
    }

    if (typeof apiMessage === 'string' && apiMessage.trim()) {
      return apiMessage;
    }

    if (
      apiMessage &&
      typeof apiMessage === 'object' &&
      typeof apiMessage.message === 'string' &&
      apiMessage.message.trim()
    ) {
      return apiMessage.message;
    }

    if (typeof candidate?.message === 'string' && candidate.message.trim()) {
      return candidate.message;
    }

    return fallback;
  }

  private storeTokenFromResponse(response: AdminAuthSuccessResponse): string {
    const token = response.data?.accessToken ?? '';
    const user = response.data?.user;

    if (!token) {
      throw new Error(response.message || 'Authentication failed. Token not found in response.');
    }

    this.setToken(token);
    if (user) {
      sessionStorage.setItem(this.userStorageKey, JSON.stringify(user));
    }
    return token;
  }
}
